// MIT License - Copyright (c) 2026 Doug McNall

/**
 * Progression System
 * Tracks player rank and achievements
 */

export const CAPTAIN_RANKS = [
  { 
    rank: 'Cadet',
    level: 1,
    systems: 5,
    credits: 10000,
    combatWins: 0,
    description: 'Novice explorer'
  },
  { 
    rank: 'Ensign',
    level: 2,
    systems: 10,
    credits: 25000,
    combatWins: 5,
    description: 'Junior officer'
  },
  { 
    rank: 'Lieutenant',
    level: 3,
    systems: 20,
    credits: 75000,
    combatWins: 15,
    description: 'Experienced pilot'
  },
  { 
    rank: 'Commander',
    level: 4,
    systems: 40,
    credits: 200000,
    combatWins: 30,
    description: 'Veteran commander'
  },
  { 
    rank: 'Captain',
    level: 5,
    systems: 75,
    credits: 500000,
    combatWins: 50,
    description: 'Master of the stars'
  },
  { 
    rank: 'Admiral',
    level: 6,
    systems: 150,
    credits: 1500000,
    combatWins: 100,
    description: 'Legendary explorer'
  }
];

export class ProgressionSystem {
  /**
   * Get current rank based on stats
   */
  static getRank(stats) {
    const { systemsVisited = 0, credits = 0, combatWins = 0 } = stats;
    
    // Find highest rank player qualifies for
    for (let i = CAPTAIN_RANKS.length - 1; i >= 0; i--) {
      const rank = CAPTAIN_RANKS[i];
      if (
        systemsVisited >= rank.systems &&
        credits >= rank.credits &&
        combatWins >= rank.combatWins
      ) {
        return rank;
      }
    }
    
    return CAPTAIN_RANKS[0]; // Default to Cadet
  }
  
  /**
   * Get next rank requirements
   */
  static getNextRank(currentRank) {
    const currentIndex = CAPTAIN_RANKS.findIndex(r => r.rank === currentRank.rank);
    
    if (currentIndex === -1 || currentIndex === CAPTAIN_RANKS.length - 1) {
      return null; // Max rank
    }
    
    return CAPTAIN_RANKS[currentIndex + 1];
  }
  
  /**
   * Calculate progress to next rank (0-100%)
   */
  static getProgressToNextRank(stats, currentRank) {
    const nextRank = this.getNextRank(currentRank);
    
    if (!nextRank) return 100; // Max rank
    
    const { systemsVisited = 0, credits = 0, combatWins = 0 } = stats;
    
    // Calculate progress for each requirement
    const systemsProgress = Math.min(100, (systemsVisited / nextRank.systems) * 100);
    const creditsProgress = Math.min(100, (credits / nextRank.credits) * 100);
    const combatProgress = Math.min(100, (combatWins / nextRank.combatWins) * 100);
    
    // Average of all three
    return Math.floor((systemsProgress + creditsProgress + combatProgress) / 3);
  }
  
  /**
   * Get detailed requirements for next rank
   */
  static getRequirements(stats, currentRank) {
    const nextRank = this.getNextRank(currentRank);
    
    if (!nextRank) {
      return {
        complete: true,
        message: 'Maximum rank achieved!'
      };
    }
    
    const { systemsVisited = 0, credits = 0, combatWins = 0 } = stats;
    
    return {
      complete: false,
      nextRank: nextRank.rank,
      requirements: [
        {
          name: 'Systems Visited',
          current: systemsVisited,
          required: nextRank.systems,
          complete: systemsVisited >= nextRank.systems
        },
        {
          name: 'Credits Earned',
          current: credits,
          required: nextRank.credits,
          complete: credits >= nextRank.credits
        },
        {
          name: 'Combat Victories',
          current: combatWins,
          required: nextRank.combatWins,
          complete: combatWins >= nextRank.combatWins
        }
      ]
    };
  }
  
  /**
   * Check if player just ranked up
   */
  static checkRankUp(oldStats, newStats) {
    const oldRank = this.getRank(oldStats);
    const newRank = this.getRank(newStats);
    
    if (newRank.level > oldRank.level) {
      return {
        rankedUp: true,
        oldRank: oldRank.rank,
        newRank: newRank.rank,
        rewards: this.getRankUpRewards(newRank)
      };
    }
    
    return { rankedUp: false };
  }
  
  /**
   * Get rewards for ranking up
   */
  static getRankUpRewards(rank) {
    const rewards = {
      credits: rank.level * 5000,
      reputation: rank.level * 10,
      unlocks: []
    };
    
    // Special unlocks per rank
      // eslint-disable-next-line default-case
    switch (rank.rank) {
      case 'Ensign':
        rewards.unlocks.push('Advanced Scanning');
        break;
      case 'Lieutenant':
        rewards.unlocks.push('Warp Factor 7');
        break;
      case 'Commander':
        rewards.unlocks.push('Quantum Torpedoes');
        break;
      case 'Captain':
        rewards.unlocks.push('Warp Factor 9');
        break;
      case 'Admiral':
        rewards.unlocks.push('Legendary Status');
        break;
    }
    
    return rewards;
  }
}

export default ProgressionSystem;
